import { Button, Form, Input, InputNumber, Modal, Select, Space, Table } from 'antd';
import {
  DeleteButton,
  EditButton,
  List,
  SaveButton,
  useEditableTable,
  useForm,
  useTable,
} from '@refinedev/antd';
import {
  IExperienceReservationSlot,
  IExperienceReservationSlotFilterVariables,
} from '../../../interfaces/experience';
import { HttpError, LogicalFilter, useCreate, useResource } from '@refinedev/core';
import DatePicker from '../../../components/form-item/DatePicker';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';

const fields = [
  'id',
  'name',
  'date',
  'startTime',
  'endTime',
  'plan',
  'capacity',
  'reservedSeatCount',
  'available',
  'price',
  'ageLimit',
  'place',
];

const AgeLimit = {
  over3: '3歳以上',
  preschooler: '未就学児',
  elementary_school: '小学生',
} as const;

export const ReservationSlotList = () => {
  const { id } = useResource();
  const {
    tableProps,
    formProps,
    isEditing,
    setId: setEditId,
    saveButtonProps,
    cancelButtonProps,
    setFilters,
    filters,
  } = useEditableTable<
    IExperienceReservationSlot,
    HttpError,
    IExperienceReservationSlotFilterVariables
  >({
    resource: 'experienceReservationSlots',
    syncWithLocation: true,
    filters: {
      initial: [
        {
          field: 'date',
          operator: 'eq',
          value: new Date()
            .toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })
            .split('/')
            .join('-'),
        },
      ],
      permanent: [
        {
          field: 'eventId',
          operator: 'eq',
          value: id,
        },
      ],
    },
    sorters: {
      initial: [
        {
          field: 'date',
          order: 'asc',
        },
        {
          field: 'startTimeInt',
          order: 'asc',
        },
        {
          field: 'endTimeInt',
          order: 'asc',
        },
      ],
    },
    pagination: {
      mode: 'off',
    },
    meta: {
      fields,
    },
    mutationMeta: {
      fields: [{ experienceReservationSlot: fields }],
    },
  });
  const { formProps: createFormProps, onFinish: onFinishCreate } = useForm({
    resource: 'experienceReservationSlots',
    action: 'create',
    redirect: false,
    meta: {
      fields,
    },
    mutationMeta: {
      fields: [{ experienceReservationSlot: fields }],
    },
  });

  const getDateFilter = () => {
    const date = (filters as LogicalFilter[]).find((filter) => filter.field === 'date')?.value;
    return date ? new Date(date) : null;
  };

  const setDateFilter = (date: Date) => {
    setFilters([
      {
        field: 'date',
        operator: 'eq',
        value: date
          .toLocaleDateString('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' })
          .split('/')
          .join('-'),
      },
    ]);
  };

  const clearDateFilter = () => {
    console.log('clearDateFilter');
    setFilters([], 'replace');
  };

  const handleOnFinishCreate = async (values: any) => {
    const date = getDateFilter();

    if (!date) {
      Modal.error({ content: '日付を選択してください' });
      return;
    }
    await onFinishCreate({
      ...values,
      eventId: id,
      date: date.toISOString().split('T')[0],
    });
  };

  const Header = () => (
    <Space data-test-id="ReservationSlotListHeader">
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => {
          const date = getDateFilter();
          if (date) {
            date.setDate(date.getDate() - 1);
            setDateFilter(date);
          }
        }}
      />
      <DatePicker
        picker="date"
        value={getDateFilter()}
        onChange={(date) => {
          if (date) {
            setDateFilter(date);
          } else {
            clearDateFilter();
          }
        }}
      />
      <Button
        icon={<ArrowRightOutlined />}
        onClick={() => {
          const date = getDateFilter();
          if (date) {
            date.setDate(date.getDate() + 1);
            setDateFilter(date);
          }
        }}
      />
    </Space>
  );

  const CreateForm = () => (
    <Form
      data-test-id="CreateForm"
      {...createFormProps}
      onFinish={handleOnFinishCreate}
      layout="vertical"
      size="small"
    >
      <Space>
        <Form.Item name="name" label="名">
          <Input />
        </Form.Item>
        <Form.Item name="startTime" label="開始時間">
          <Input />
        </Form.Item>
        <Form.Item name="endTime" label="終了時間">
          <Input />
        </Form.Item>
        <Form.Item name="plan" label="プラン">
          <Input />
        </Form.Item>
        <Form.Item name="capacity" label="上限数">
          <InputNumber />
        </Form.Item>
        <Form.Item name="price" label="料金">
          <Input />
        </Form.Item>
        <Form.Item name="place" label="場所">
          <Input />
        </Form.Item>
        <Form.Item label="年齢制限" name="ageLimit">
          <Select
            style={{ width: 120 }}
            options={[
              { label: '3歳以上', value: 'over3' },
              { label: '未就学児', value: 'preschooler' },
              { label: '小学生', value: 'elementary_school' },
            ]}
          />
        </Form.Item>
        <Button type="primary" htmlType="submit">
          追加
        </Button>
      </Space>
    </Form>
  );

  return (
    <List
      resource="experienceReservationSlots"
      breadcrumb={false}
      canCreate={true}
      headerButtons={<Header />}
    >
      <CreateForm />
      <Form data-test-id="ReservationSlotList" {...formProps}>
        <Table
          {...tableProps}
          rowKey="id"
          onRow={(record) => ({
            onClick: (event: any) => {
              if (event.target.nodeName === 'TD') {
                setEditId && setEditId(record.id);
              }
            },
          })}
        >
          <Table.Column<IExperienceReservationSlot>
            title="名(管理用)"
            dataIndex="name"
            render={(value, record) =>
              isEditing(record.id) ? (
                <Form.Item name="name">
                  <Input />
                </Form.Item>
              ) : (
                value
              )
            }
          />
          {!getDateFilter() && (
            <Table.Column<IExperienceReservationSlot>
              title="日付"
              dataIndex="date"
              render={(value, record) =>
                isEditing(record.id) ? (
                  <Form.Item name="date">
                    <Input type="date" />
                  </Form.Item>
                ) : (
                  value
                )
              }
            />
          )}
          <Table.Column<IExperienceReservationSlot>
            title="開始時間"
            dataIndex="startTime"
            render={(value, record) =>
              isEditing(record.id) ? (
                <Form.Item name="startTime">
                  <Input />
                </Form.Item>
              ) : (
                value
              )
            }
          />
          <Table.Column<IExperienceReservationSlot>
            title="終了時間"
            dataIndex="endTime"
            render={(value, record) =>
              isEditing(record.id) ? (
                <Form.Item name="endTime">
                  <Input />
                </Form.Item>
              ) : (
                value
              )
            }
          />
          <Table.Column<IExperienceReservationSlot>
            title="プラン"
            dataIndex="plan"
            render={(value, record) =>
              isEditing(record.id) ? (
                <Form.Item name="plan">
                  <Input />
                </Form.Item>
              ) : (
                value
              )
            }
          />
          <Table.Column<IExperienceReservationSlot>
            title="上限数"
            dataIndex="capacity"
            render={(value, record) =>
              isEditing(record.id) ? (
                <Form.Item name="capacity">
                  <Input />
                </Form.Item>
              ) : (
                value
              )
            }
          />
          <Table.Column<IExperienceReservationSlot> title="予約数" dataIndex="reservedSeatCount" />
          <Table.Column<IExperienceReservationSlot> title="残数" dataIndex="available" />
          <Table.Column<IExperienceReservationSlot>
            title="料金"
            dataIndex="price"
            render={(value, record) =>
              isEditing(record.id) ? (
                <Form.Item name="price">
                  <InputNumber />
                </Form.Item>
              ) : (
                value
              )
            }
          />
          <Table.Column<IExperienceReservationSlot>
            title="場所"
            dataIndex="place"
            render={(value, record) =>
              isEditing(record.id) ? (
                <Form.Item name="place">
                  <Input />
                </Form.Item>
              ) : (
                value
              )
            }
          />
          <Table.Column<IExperienceReservationSlot>
            title="年齢制限"
            dataIndex="ageLimit"
            render={(value: IExperienceReservationSlot['ageLimit'], record) =>
              isEditing(record.id) ? (
                <Form.Item name="ageLimit">
                  <Select
                    options={[
                      { label: '3歳以上', value: 'over3' },
                      { label: '未就学児', value: 'preschooler' },
                      { label: '小学生', value: 'elementary_school' },
                    ]}
                  />
                </Form.Item>
              ) : (
                AgeLimit[value]
              )
            }
          />
          <Table.Column<IExperienceReservationSlot>
            dataIndex="actions"
            render={(_, record) =>
              isEditing(record.id) ? (
                <Space>
                  <SaveButton {...saveButtonProps} hideText size="small" />
                  <Button {...cancelButtonProps} size="small">
                    Cancel
                  </Button>
                </Space>
              ) : (
                <DeleteButton
                  resource="experienceReservationSlots"
                  recordItemId={record.id}
                  hideText
                  confirmOkText="削除しますか？"
                />
              )
            }
          />
        </Table>
      </Form>
    </List>
  );
};
